import { Box, Typography, Button, styled, Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { useState, useEffect } from 'react';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import { WithoutHeader, DesktopLayout } from 'src/layout';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import useUserStore from 'src/stores/user';
import AccountsService from 'src/services/UserSevice';
import CustomSwitch from 'src/components/CustomSwitch';
import EventService from 'src/services/EventService';
import Title from 'src/components/Title';

const CustomTextField = styled(Box)(({ theme }) => ({
    backgroundColor: 'var(--lm-surface)',
    borderRadius: '16px',
}));

// Компонент скелетона для блока подключения к Telegram
const TelegramConnectionSkeleton = () => (
    <Box
        mt={2}
        mb={2}
        p={2}
        bgcolor="var(--lm-surface)"
        borderRadius={3}
        boxShadow={0}
        display="flex"
        flexDirection="row"
        alignItems="flex-start"
        gap={2}
    >
        <Box
            flex={1}
            display="flex"
            flexDirection="column"
            gap={1}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
            >
                <Skeleton
                    variant="text"
                    width="80%"
                    height={32}
                    sx={{ bgcolor: 'var(--lm-canvas)' }}
                />
                <Skeleton
                    variant="rectangular"
                    width={120}
                    height={36}
                    sx={{ borderRadius: 2, bgcolor: 'var(--lm-canvas)' }}
                />
            </Box>
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={1}
            >
                <Skeleton
                    variant="rectangular"
                    width={40}
                    height={20}
                    sx={{ borderRadius: 1, bgcolor: 'var(--lm-canvas)' }}
                />
                <Skeleton
                    variant="text"
                    width={150}
                    height={20}
                    sx={{ bgcolor: 'var(--lm-canvas)' }}
                />
            </Box>
        </Box>
    </Box>
);

export default function NotificationsScreen() {
    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useIsMobileSize();

    const userInfo = useUserStore((state) => ({
        tg_notifications_enabled: state.tg_notifications_enabled,
        is_telegram_connected: state.is_telegram_connected,
    }));

    const [isTelegramConnected, setIsTelegramConnected] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [isLoadingTelegramStatus, setIsLoadingTelegramStatus] = useState(true);

    const getBindLinkMutation = useMutation(() => AccountsService.getTelegramBindLink(), {
        onError: () => {
            enqueueSnackbar('Ошибка при получении ссылки', { variant: 'error' });
        },
    });

    const handleConnectBot = async () => {
        try {
            const res = await getBindLinkMutation.mutateAsync();
            if (res && typeof res.telegram_bind_link === 'string') {
                if (typeof res.is_telegram_connected === 'boolean') {
                    setIsTelegramConnected(res.is_telegram_connected);
                }
                window.open(res.telegram_bind_link, '_blank', 'noopener,noreferrer');
            } else {
                enqueueSnackbar('Ошибка при получении ссылки', { variant: 'error' });
            }
        } catch {
            // Ошибка уже обработана в onError
        }
    };

    useEffect(() => {
        setNotificationsEnabled(!!userInfo.tg_notifications_enabled);
    }, [userInfo.tg_notifications_enabled]);

    useEffect(() => {
        (async () => {
            try {
                const res = await AccountsService.getTelegramBindLink();
                if (res && typeof res.is_telegram_connected === 'boolean') {
                    setIsTelegramConnected(res.is_telegram_connected);
                }
            } catch {
            } finally {
                setIsLoadingTelegramStatus(false);
            }
        })();
    }, []);

    const renderTelegramBlock = () => {
        if (isLoadingTelegramStatus) {
            return <TelegramConnectionSkeleton />;
        }

        return (
            <Box
                mt={2}
                mb={2}
                p={2}
                bgcolor="var(--lm-surface)"
                borderRadius={3}
                boxShadow={0}
                display="flex"
                flexDirection="row"
                alignItems="flex-start"
                gap={2}
            >
                <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1}
                    >
                        <Typography
                            variant="h3"
                            color="text.primary"
                            sx={{ fontWeight: 600, mb: 0 }}
                        >
                            Доставка лидов в Telegram
                        </Typography>
                        {isTelegramConnected ? (
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.7}
                                sx={{ mr: 1 }}
                            >
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: 'var(--lm-success-ink)',
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, color: 'var(--lm-success-ink)' }}
                                >
                                    Подключён
                                </Typography>
                            </Box>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConnectBot}
                                disabled={getBindLinkMutation.isLoading}
                                sx={{ ml: 2 }}
                            >
                                Подключить
                            </Button>
                        )}
                    </Box>
                    {isTelegramConnected && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={1}
                        >
                            <CustomSwitch
                                inputProps={{ 'aria-label': 'Уведомления в Telegram' }}
                                checked={notificationsEnabled}
                                onChange={(e) => {
                                    const enabled = e.target.checked;
                                    setNotificationsEnabled(enabled);

                                    // Используем try-catch для лучшей обработки ошибок
                                    (async () => {
                                        try {
                                            console.log('Отправляем запрос на обновление уведомлений:', {
                                                tg_notifications_enabled: enabled,
                                            });

                                            // Обновляем только уведомления, не трогая другие поля
                                            await AccountsService.updateAccount({
                                                tg_notifications_enabled: enabled,
                                            });

                                            console.log('Уведомления успешно обновлены');

                                            // Обновляем глобальный стор для синхронизации с меню
                                            useUserStore.getState().updateNotificationSettings({
                                                tg_notifications_enabled: enabled,
                                            });

                                            // Отправляем событие для обновления анимации в меню
                                            EventService.dispatch('NOTIFICATIONS_UPDATED', { enabled });

                                            enqueueSnackbar(
                                                enabled
                                                    ? 'Уведомления в Telegram включены'
                                                    : 'Уведомления в Telegram выключены',
                                                { variant: 'success' }
                                            );
                                        } catch (error) {
                                            // Возвращаем состояние обратно при ошибке
                                            setNotificationsEnabled(!enabled);
                                            console.error('Ошибка обновления уведомлений:', error);
                                            enqueueSnackbar('Ошибка при обновлении уведомлений', { variant: 'error' });
                                        }
                                    })();
                                }}
                            />
                            <Typography variant="body1">Уведомления в Telegram</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const renderContent = () => (
        <LoadingWrapper isLoading={false}>
            <Box
                pt={2}
                minHeight="100%"
                display="flex"
                flexDirection="column"
            >
                <Box mb={1.5}>
                    <Title>Уведомления</Title>
                </Box>
                {/* --- Блок Telegram --- */}
                {renderTelegramBlock()}
                {/* --- Конец блока Telegram --- */}
            </Box>
        </LoadingWrapper>
    );

    if (!isMobile) {
        return (
            <DesktopLayout hideRightSidebar>
                <Box sx={{ maxWidth: 900 }}>{renderContent()}</Box>
            </DesktopLayout>
        );
    }

    return (
        <WithoutHeader
            showBack
            showMyHeader
            title="Уведомления"
        >
            {renderContent()}
        </WithoutHeader>
    );
}
